<script setup lang="ts">
import { computed } from 'vue'
import i18n from '@/scripts/app-configs/i18n-config'
import { ICONS } from '@/components/drawers/MemberDrawer/constants/member-goals-icons'
import type { Goal } from '@/components/drawers/MemberDrawer/types/member-drawer.types'

const props = withDefaults(defineProps<{
  items?: Goal[]
}>(), {
  items: () => [],
})

const emit = defineEmits<{
  (e: 'copy-to-clipboard', content: string): void;
}>()

const orderedItems = computed(() => {
  return props.items.toSorted((a, b) => ((a.is_achieved === b.is_achieved) ? 0 : a.is_achieved ? -1 : 1))
})

const buttonLabel = computed(() => {
  const total = props.items.length
  const achieved = props.items.filter((item) => item.is_achieved).length

  if (!total) return i18n.t('SIDEBAR.GOALS_NONE')
  if (total === 1) return i18n.t('SIDEBAR.GOALS_VIEW_SINGULAR', { achieved })

  return i18n.t('SIDEBAR.GOALS_VIEW_PLURAL', { achieved, total })
})

const translateGoal = ({ key, value }: { key: string, value: string | number }) => {
  return i18n.t(`personal_goal_${key}`, { value })
}

const handleCopyGoals = () => {
  const output = orderedItems.value.map((goal) => `• ${translateGoal(goal)}`).join('\n')

  emit('copy-to-clipboard', output)
}

const getIconPath = (key: string) => {
  const icon = ICONS[key as keyof typeof ICONS] ?? 'goal'

  return require(`@ui-kit/assets/images/goals/${icon}.svg`)
}
</script>

<template>
  <sword-popover-wuk class="relative ml-2 inline-block" :distance="10" placement="bottom-end">
    <sword-button-wuk
      theme="secondary"
      icon="target-arrow"
      size="small"
      side="left"
      :text="buttonLabel"
      :disabled="!items.length"
      class="!mt-2"
    />
    <template #content>
      <main class="w-[25rem] py-2">
        <article v-for="(item, index) of orderedItems" :key="index" class="flex gap-4 border-b border-background-dark px-4 py-3 last:border-b-0">
          <figure class="m-0 flex h-14 min-w-14 items-center justify-center rounded-xl bg-background-normal">
            <inline-svg :src="getIconPath(item.key)" class="size-8" />
          </figure>
          <div class="flex flex-col items-start gap-1">
            <h6 class="flex text-sm font-normal text-[#1F2937]">{{ translateGoal(item) }}</h6>
            <goal-status-pill :status="item.is_achieved ? 'achieved' : 'not_achieved'" />
          </div>
        </article>
        <div class="flex justify-center p-2.5">
          <sword-button-wuk
            v-close-popper
            theme="secondary"
            icon="copy"
            size="small"
            side="left"
            :text="items.length > 1 ? $t('SIDEBAR.GOALS_COPY_ALL') : $t('SIDEBAR.GOALS_COPY_1')"
            class="w-full"
            @click.native="handleCopyGoals"
          />
        </div>
      </main>
    </template>
  </sword-popover-wuk>
</template>
